import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Layers, 
  Camera, 
  TrendingUp, 
  Building2,
  CheckCircle2,
  ArrowRight,
  Star,
  Shield,
  Zap,
  FileText,
  BarChart3,
  ChevronUp
} from 'lucide-react';
import logo from '@/assets/Light_mode_large_logo.png';
import navLogo from '@/assets/signup_logo_80.png';

export default function Home() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoadingAuth } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Carousel images
  const carouselImages = [
    '/carousel/WhatsApp%20Image%202026-02-23%20at%2011.22.11.jpeg',
    '/carousel/WhatsApp%20Image%202026-02-23%20at%2012.02.09.jpeg',
    '/carousel/WhatsApp%20Image%202026-02-23%20at%2012.26.46.jpeg',
  ];

  // Redirect if already logged in
  useEffect(() => {
    if (!isLoadingAuth && isAuthenticated) {
      navigate('/EstateDashboard');
    }
  }, [isAuthenticated, isLoadingAuth, navigate]);

  // Carousel auto-rotate effect
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === carouselImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000); // Change image every 5 seconds

    return () => clearInterval(interval);
  }, [carouselImages.length]);

  // Scroll to top button visibility
  useEffect(() => {
    const handleScroll = () => {
      // Show button when user scrolls down 400px from the top
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Structured data for SEO (JSON-LD)
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "FixMyHood",
    "applicationCategory": "BusinessApplication",
    "description": "Estate management platform for tracking and resolving infrastructure issues in gated communities and residential estates",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "ZAR"
    },
    "featureList": [
      "Report estate issues with photos and descriptions",
      "Real-time issue status tracking",
      "Estate analytics and reporting",
      "Comprehensive estate management dashboard",
      "Monthly report generation",
      "Issue categorization and filtering"
    ]
  };

  const features = [
    {
      icon: Building2,
      title: "Estate Dashboard",
      description: "Comprehensive dashboard to manage all estate issues, track progress, and analyze trends"
    },
    {
      icon: Camera,
      title: "Issue Reporting",
      description: "Estate managers report issues with photos and detailed descriptions for comprehensive tracking"
    },
    {
      icon: Layers,
      title: "Issue Categories",
      description: "Organize and filter issues by type - plumbing, electrical, landscaping, security, and more"
    },
    {
      icon: BarChart3,
      title: "Analytics & Insights",
      description: "View monthly reports, track resolution rates, and identify problem areas in your estate"
    },
    {
      icon: TrendingUp,
      title: "Status Tracking",
      description: "Update issue status in real-time and keep residents informed of progress"
    },
    {
      icon: FileText,
      title: "Monthly Reports",
      description: "Generate professional monthly reports for estate committees and HOA meetings"
    }
  ];

  const trustBadges = [
    { text: "30-Day Free Trial" },
    { text: "No Credit Card" },
    { text: "Setup in Minutes" },
    { text: "All Features Included" }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Create Your Estate",
      description: "Set up your estate profile within minutes "
    },
    {
      step: "2",
      title: "Estate Manager Report",
      description: "Managers report & record issues within the estate"
    },
    {
      step: "3",
      title: "Manage & Track",
      description: "Review reports, update statuses, and assign to contractors"
    },
    {
      step: "4",
      title: "Analyze & Report",
      description: "Generate insights and monthly reports for your estate committee"
    }
  ];

  const pricingTiers = [
    {
      id: 'small',
      name: 'Small Estate',
      price: 'R599',
      period: '/month',
      description: 'Up to 50 homes',
      icon: Building2,
      popular: false
    },
    {
      id: 'medium',
      name: 'Medium Estate',
      price: 'R1,149',
      period: '/month',
      description: '51–200 homes',
      icon: Zap,
      popular: true
    },
    {
      id: 'large',
      name: 'Large Estate',
      price: 'R1,999',
      period: '/month',
      description: '201+ homes',
      icon: Shield,
      popular: false
    }
  ];

  const benefits = [
    {
      icon: Zap,
      title: "Streamlined Management",
      description: "Centralize all estate issues in one platform for efficient tracking"
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "Enterprise-grade security with Google OAuth and encrypted data"
    },
    {
      icon: CheckCircle2,
      title: "Organized Management",
      description: "Keep all estate issues organized and categorized for efficient resolution"
    },
    {
      icon: BarChart3,
      title: "Data-Driven Insights",
      description: "Analytics help identify trends and prioritize maintenance budgets"
    }
  ];

  if (isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      {/* Inject structured data for SEO */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
        {/* Navigation */}
        <nav className="border-b bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50 border-emerald-100 dark:border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-3">
                <img 
                  src={navLogo} 
                  alt="FixMyHood Logo" 
                  className="h-14 w-auto"
                />
                <span className="text-2xl font-bold text-slate-900 dark:text-white">
                  FixMyHood
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    const pricingSection = document.getElementById('pricing');
                    pricingSection?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="hidden md:inline-flex rounded-[24px] md:rounded-[40px]"
                >
                  Pricing
                </Button>

                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/faq')}
                  className="hidden md:inline-flex rounded-[24px] md:rounded-[40px]"
                >
                  FAQ
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/Login')}
                  className="hidden sm:inline-flex rounded-[24px] md:rounded-[40px]"
                >
                  Sign In
                </Button>
                <Button 
                  onClick={() => navigate('/Signup')}
                  className="gap-2 rounded-[24px] md:rounded-[40px] bg-[#1883FF] hover:bg-[#0066CC]"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative overflow-hidden min-h-[500px] sm:min-h-[600px] lg:min-h-[700px]">
          {/* Background Image Carousel */}
          <div className="absolute inset-0 z-0">
            {carouselImages.map((image, index) => (
              <img
                key={index}
                src={image}
                alt={`Estate ${index + 1}`}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                  index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                }`}
              />
            ))}
            {/* Dark overlay for text readability */}
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 via-slate-900/40 to-slate-900/60"></div>
          </div>

          {/* Hero Content - On top of carousel */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-4 sm:mb-6 text-white drop-shadow-lg">
                Estate Management.
                <br />
                <span className="bg-gradient-to-r from-[#1883FF] to-green-400 bg-clip-text text-transparent">Simplified & Streamlined.</span>
              </h2>
              
              <p className="text-base sm:text-lg text-gray-100 mb-8 sm:mb-10 max-w-2xl mx-auto drop-shadow-md px-4">
                The complete platform for estate managers to track, manage, and resolve 
                infrastructure issues across gated communities and residential estates.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-stretch sm:items-center px-4">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/Signup')}
                  className="gap-2 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 rounded-[24px] md:rounded-[40px] bg-[#1883FF] hover:bg-[#0066CC] shadow-lg shadow-[#1883FF]/30 w-full sm:w-auto"
                >
                  Start Your 30-Day Free Trial <ArrowRight className="w-5 h-5" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/Login')}
                  className="gap-2 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 rounded-[24px] md:rounded-[40px] border-2 border-green-600 bg-green-600 text-white hover:bg-transparent hover:text-green-400 hover:border-green-400 backdrop-blur-sm shadow-lg transition-all w-full sm:w-auto"
                >
                  <Building2 className="w-5 h-5" />
                  Estate Manager Login
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8 mt-12 sm:mt-16 md:mt-20 px-4">
                {trustBadges.map((badge, index) => (
                  <div key={index} className="flex items-center justify-center gap-2 backdrop-blur-sm bg-white/10 rounded-xl p-3 sm:p-4 border border-white/20">
                    <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-green-400 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-medium text-white">
                      {badge.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white dark:bg-slate-900" id="features">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                Everything You Need to Manage Your Estate
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
                Powerful tools designed to streamline estate management and issue resolution
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Card key={index} className="border-2 hover:border-[#1883FF] transition-colors hover:shadow-lg hover:shadow-[#1883FF]/10">
                    <CardContent className="pt-6">
                      <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#1883FF]/10 to-green-600/10 flex items-center justify-center mb-4 border border-[#1883FF]/20">
                        <Icon className="w-6 h-6 text-[#1883FF]" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                How FixMyHood Works
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground px-4">
                From report to resolution in four simple steps
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {howItWorks.map((step, index) => (
                <div key={index} className="relative">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#1883FF] to-green-600 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#1883FF]/20">
                      {step.step}
                    </div>
                    <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </div>
                  {index < howItWorks.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-[calc(50%+2rem)] w-[calc(100%-4rem)] h-0.5 bg-gradient-to-r from-[#1883FF]/20 to-green-600/20"></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 bg-white dark:bg-slate-900" id="pricing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                Simple, Transparent Pricing
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground mb-2 px-4">
                Choose the plan that fits your estate size
              </p>
              <p className="text-base text-muted-foreground px-4">
                Start with a <span className="font-semibold text-[#1883FF]">30-day free trial</span> — no credit card required
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
              {pricingTiers.map((tier) => {
                const Icon = tier.icon;
                return (
                  <Card
                    key={tier.id}
                    className={`relative p-6 ${
                      tier.popular
                        ? 'border-2 border-[#1883FF] shadow-xl'
                        : 'border-2 border-slate-200 dark:border-slate-700'
                    }`}
                  >
                    {tier.popular && (
                      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                        <Badge className="bg-[#1883FF] text-white shadow-lg">
                          Most Popular
                        </Badge>
                      </div>
                    )}
                    
                    <div className="text-center">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#1883FF]/10 to-green-600/10 flex items-center justify-center mx-auto mb-4 border border-[#1883FF]/20">
                        <Icon className="w-6 h-6 text-[#1883FF]" />
                      </div>
                      
                      <h3 className="text-xl font-bold mb-2">{tier.name}</h3>
                      <p className="text-sm text-muted-foreground mb-4">{tier.description}</p>
                      
                      <div className="mb-6">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold text-slate-900 dark:text-white">
                            {tier.price}
                          </span>
                          <span className="text-muted-foreground">{tier.period}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">after free trial</p>
                      </div>

                      <Button
                        onClick={() => navigate('/Signup')}
                        className={`w-full rounded-[24px] md:rounded-[40px] ${
                          tier.popular
                            ? 'bg-[#1883FF] hover:bg-[#0066CC]'
                            : 'bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-100 dark:text-slate-900'
                        }`}
                      >
                        Start Free Trial
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>

            <p className="text-center text-xs sm:text-sm text-muted-foreground mt-6 sm:mt-8 px-4">
              All plans include full access to all features: issue tracking, contractor management, monthly reports, analytics, and more
            </p>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4">
                Why Estate Managers Choose FixMyHood
              </h2>
              <p className="text-lg sm:text-xl text-muted-foreground px-4">
                Purpose-built for estate management excellence
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <div key={index} className="text-center">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-600/10 to-emerald-600/10 flex items-center justify-center mx-auto mb-4 border-2 border-green-200 dark:border-green-800">
                      <Icon className="w-8 h-8 text-green-600 dark:text-green-500" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                    <p className="text-muted-foreground text-sm">{benefit.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Testimonial Section - Commented out until we have real testimonials */}
        {/* <section className="py-16 sm:py-20 bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="flex justify-center mb-4 sm:mb-6">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 fill-green-500" />
              ))}
            </div>
            <blockquote className="text-lg sm:text-2xl md:text-3xl font-medium mb-4 sm:mb-6 px-4">
              "FixMyHood transformed how we manage our estate. I can track every issue, 
              monitor trends, and provide detailed reports to our committee - all in one place."
            </blockquote>
            <div className="text-muted-foreground">
              <p className="font-semibold">Sarah Johnson</p>
              <p>Estate Manager, Sunset Gardens Estate</p>
            </div>
          </div>
        </section> */}

        {/* CTA Section */}
        <section className="py-16 sm:py-20 bg-gradient-to-br from-[#1883FF] to-green-600 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">
              Ready to Streamline Your Estate Management?
            </h2>
            <p className="text-base sm:text-xl mb-8 sm:mb-10 opacity-90">
              Join estate managers who are transforming how they track and resolve infrastructure issues.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Button 
                size="lg" 
                variant="secondary"
                onClick={() => navigate('/Signup')}
                className="gap-2 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 rounded-[24px] md:rounded-[40px] bg-white text-[#1883FF] hover:bg-gray-100 w-full sm:w-auto"
              >
                Start Your 30-Day Free Trial <ArrowRight className="w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/Login')}
                className="gap-2 text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 bg-transparent text-white border-white hover:bg-white/10 rounded-[24px] md:rounded-[40px] w-full sm:w-auto"
              >
                Sign In
              </Button>
            </div>
            
            <div className="mt-8 sm:mt-12 flex flex-wrap justify-center items-center gap-4 sm:gap-6 md:gap-8 text-xs sm:text-sm opacity-75">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>30-day free trial</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5" />
                <span>Setup in 2 minutes</span>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-slate-300 py-12 border-t-4 border-green-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 mb-8">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <img 
                    src={logo} 
                    alt="FixMyHood Logo" 
                    className="h-10 w-auto"
                  />
                </div>
                <p className="text-sm">
                  The complete estate management platform for tracking and resolving infrastructure issues.
                </p>
              </div>
              
              <div>
                <h4 className="font-semibold text-white mb-4">Product</h4>
                <ul className="space-y-2 text-sm">
                  <li><button onClick={() => navigate('/Signup')} className="hover:text-white transition-colors">Create Estate</button></li>
                  <li><button onClick={() => navigate('/Login')} className="hover:text-white transition-colors">Estate Manager Login</button></li>
                  <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                  <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-white mb-4">Company</h4>
                <ul className="space-y-2 text-sm">
                  <li><button onClick={() => navigate('/PrivacyPolicy')} className="hover:text-white transition-colors">Privacy Policy</button></li>
                  <li><button onClick={() => navigate('/TermsOfService')} className="hover:text-white transition-colors">Terms of Service</button></li>
                  <button 
                    onClick={() => navigate('/faq')} 
                    className="hover:text-white transition-colors"
                  >
                    FAQ
                  </button>
                </ul>
              </div>
              
              <div>
                <h4 className="font-semibold text-white mb-4">Connect</h4>
                <p className="text-sm mb-3">
                  Have questions? We'd love to hear from you.
                </p>
                <a 
                  href="mailto:support@fixmyhood.app" 
                  className="text-sm text-[#1883FF] hover:text-blue-400 transition-colors break-all"
                >
                  support@fixmyhood.app
                </a>
              </div>
            </div>
            
            <div className="border-t border-slate-800 pt-8 text-sm text-center">
              <p>&copy; {new Date().getFullYear()} FixMyHood. All rights reserved.</p>
            </div>
          </div>
        </footer>

        {/* Back to Top Button */}
        {showScrollTop && (
          <button
            onClick={scrollToTop}
            className="fixed bottom-6 right-4 sm:bottom-8 sm:right-8 z-50 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-[#1883FF] to-green-600 text-white shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center group"
            aria-label="Scroll to top"
          >
            <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6 group-hover:translate-y-[-2px] transition-transform" />
          </button>
        )}
      </div>
    </>
  );
}